import { useEffect, useState } from 'react';
import '../../../../styles/components/charts/chart.scss';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../store/store';
import get_graph_data_api_call from '../../../../lib/api/stats/get_graph_data_api_call';
import DeviceConversionRateBarChart from './DeviceConversionRateBarChart';
import SingleDeviceConversionRateBarChart from './SingleDeviceConversionRateBarChart';
import { useSelector } from 'react-redux';
import ChartWithConfig from '../../ChartWithConfig';
import isNotEmptyString from '../../../../lib/helper-functions/isNotEmptyString';
import capitalizeFirstLetterOfEachWord from '../../../../lib/helper-functions/capitalizeFirstLetterOfEachWord';
import makeFirstLetterCapital from '../../../../lib/helper-functions/makeFirstLetterCapital';
import { defaultGraphTimeFilter } from '../../../../lib/constants/abstracts';

type Props = {
  timeFilter: string;
  customPeriodFilterDateRange: {
    start_date: string | undefined;
    end_date: string | undefined;
  };
  eventFilter: string;
};

const MainDeviceBasedCRChart = ({
  timeFilter = defaultGraphTimeFilter,
  customPeriodFilterDateRange,
  eventFilter = 'purchase',
}: Props) => {
  const { t } = useTranslation();
  const stats = useSelector((state: RootState) => state.user?.stats);
  const [params, setParams] = useState<string>(
    `?graph_type=conversion_rate&group_by=device&time_range=${timeFilter}&event_type=${eventFilter}`
  );

  const [response, setResponse] = useState<any>({
    type: 'overallDevices',
    data: {},
  });

  //Selected country
  const [selectedDevice, setSelectedDevice] = useState<string>('');

  //Comments and titles
  const [title, setTitle] = useState<string>(t('DeviceBasedConversionRate'));
  const [comment, setComment] = useState<string>(
    'TheConversionRateOfCartsToCheckoutsBasedOnDevice'
  );

  const [isBackButtonVisible, setIsBackButtonVisible] =
    useState<boolean>(false);

  const renderCharts = () => {
    if (selectedDevice === '') {
      return (
        <DeviceConversionRateBarChart
          apiResponse={response}
          setSelectedDevice={setSelectedDevice}
        />
      );
    } else if (selectedDevice !== '') {
      return (
        <SingleDeviceConversionRateBarChart
          apiResponse={response}
          onBackButtonClick={onBackButtonClick}
        />
      );
    }
  };

  /* ---------API CALL----------- */
  const getGraphData = async () => {
    try {
      // Call to get conversion rate stats by location
      const res = await get_graph_data_api_call(
        stats.ai_tracker_api_key,
        stats.ai_tracker_access_token,
        params
      );

      if (res) {
        if (selectedDevice && selectedDevice !== '') {
          setResponse({
            type: 'singleDevice',
            data: res,
          });
        } else {
          setResponse({
            type: 'overallDevices',
            data: res,
          });
        }
      } else {
      }
    } catch (error) {
      console.error('Error fetching conversion rate data:', error);
    }
  };

  useEffect(() => {
    if (selectedDevice === '') {
      const title1 =
        eventFilter === 'purchase'
          ? t('DeviceBasedConversionRate')
          : `${t('DeviceBased')} ${eventFilter} ${t('ClickThroughRate')}`;
      setTitle(capitalizeFirstLetterOfEachWord(title1));

      const title2 =
        eventFilter === 'purchase'
          ? t('TheConversionRateOfCartsToCheckoutsBasedOnDevice')
          : `${t('DeviceBased')} ${t('ClickThroughRate')} ${t(
              'of'
            )} ${eventFilter}`;

      setComment(capitalizeFirstLetterOfEachWord(title2));
    } else if (selectedDevice !== '') {
      const title1 = `${
        eventFilter === 'purchase'
          ? t('TheConversionRateOf')
          : t('ClickThroughRateOf')
      } ${selectedDevice}`;
      setTitle(capitalizeFirstLetterOfEachWord(title1));

      const title2 =
        eventFilter === 'purchase'
          ? `${t('ConversionRateCartsToCheckoutsIn')} ${selectedDevice}`
          : `${t('ClickThroughRate')} ${t('of')} ${eventFilter}`;

      setComment(capitalizeFirstLetterOfEachWord(title2));
    }
  }, [selectedDevice, eventFilter]);

  useEffect(() => {
    let dateRangeURLParam = '';
    if (timeFilter === 'custom') {
      const { start_date, end_date } = customPeriodFilterDateRange || {};
      if (start_date === undefined || end_date === undefined) {
        return;
      }
      dateRangeURLParam = `&start_date=${start_date}&end_date=${end_date}`;
    }

    let deviceURLParam = '';
    if (isNotEmptyString(selectedDevice)) {
      deviceURLParam = `&group_by_value=${selectedDevice}`;
    }

    let eventURLParam = '';
    if (isNotEmptyString(eventFilter)) {
      eventURLParam = `&event_type=${eventFilter}`;
    }

    setParams(
      `?graph_type=conversion_rate&time_range=${timeFilter}${dateRangeURLParam}&group_by=device${deviceURLParam}${eventURLParam}`
    );
  }, [timeFilter, customPeriodFilterDateRange, eventFilter, selectedDevice]);

  useEffect(() => {
    if (
      stats.ai_tracker_api_key !== '' &&
      stats.ai_tracker_access_token !== ''
    ) {
      setResponse({ type: 'load', data: [] });
      getGraphData();
    }
  }, [params, stats.ai_tracker_access_token, stats.ai_tracker_api_key]);

  const onBackButtonClick = () => {
    if (selectedDevice !== '') {
      setSelectedDevice('');
    }
  };

  useEffect(() => {
    if (selectedDevice !== '') {
      setIsBackButtonVisible(true);
    } else {
      setIsBackButtonVisible(false);
    }
  }, [selectedDevice]);

  return (
    <ChartWithConfig
      isBackButtonVisible={isBackButtonVisible}
      onBackButtonClick={onBackButtonClick}
      title={title}
      comment={comment}
    >
      {renderCharts()}
    </ChartWithConfig>
  );
};

export default MainDeviceBasedCRChart;
